/** Blank product settings — URL prefix and helpers (sidebar swap + routing). */
import { productionFromPreset } from "print-core";
import { blankHasNeckLabelPlacement } from "./neckLabel";
import { presetFor } from "./productionFromWizard";

export const BLANK_SETTINGS_PREFIX = "/products/blanks";

export function isBlankSettingsPath(pathname) {
  return /^\/products\/blanks\/[^/]+/.test(pathname || "");
}

export function parseBlankIdFromPath(pathname) {
  const m = (pathname || "").match(/^\/products\/blanks\/([^/]+)/);
  return m ? m[1] : null;
}

export const RIVERR_LAST_NON_BLANK_SETTINGS_PATH_KEY =
  "riverr_last_non_blank_settings_path";

export function blankSettingsPathFor(blankId, segment) {
  return `${BLANK_SETTINGS_PREFIX}/${blankId}/${segment}`;
}

/**
 * Section → API field ownership. Used by dirty tracking and the save
 * payload builder so a section Save only patches its own fields.
 */
export const BLANK_SECTION_FIELDS = {
  general: [
    "blankProductName",
    "blankProductBrand",
    "style",
    "blankProductDescription",
    "active",
    "sellable",
    "decorable",
    "allowNoDecoration",
    "noDecorationFee",
    "boughtByHand",
    "showOrderCostBreakdown",
    "upcharge",
    "labelsCount",
    "shippingPriceCategory",
    "productCategories",
    "visibleToCatalogTierIds",
    "primaryImage",
  ],
  "print-methods": [
    "decorationMethods",
    "decorationMethod",
    "decorationMethodPricings",
    "digitizationPrice",
    "combinePreferredPrintMethods",
    "defaultDecorationMethod",
  ],
  dimensions: ["dimensions"],
  "add-ons": ["productAddOns"],
  "neck-label": ["material", "countryOfOrigin", "careInstructions"],
  placements: [
    "placements",
    "defaultPlacement",
    "placementOrder",
    "isLongSleeve",
    "placementMockupSources",
  ],
  "design-areas": [
    "placementDetails",
    "selectedPlacementCategory",
    "placementMockupSources",
    "colorsWithImages",
    "printSpecs",
    "production",
  ],
  variants: [
    "blankVariantIds",
    "colorsWithImages",
    "defaultColor",
    "sizes",
    "blankVariants",
    "newBlankVariants",
    "updatedBlankVariants",
    "deletedBlankVariant",
    "placementMockupSources",
  ],
  "variant-mapping": [
    "blankVariantIds",
    "colorsWithImages",
    "blankVariants",
    "newBlankVariants",
    "updatedBlankVariants",
    "deletedBlankVariant",
    "costRules",
    "costOverrides",
  ],
  "suggested-retail": [
    "retailMode",
    "suggestedMarginPct",
    "retailRules",
    "retailOverrides",
    "marginOverrides",
  ],
  "supplier-match": [
    "fulfillmentBlankProductId",
    "fulfillmentSupplierId",
    "fulfillmentSupplierName",
    "fulfillmentBlankName",
    "fulfillmentVariantMap",
  ],
  mockups: [
    "mockups",
    "selectedPreviewMocks",
    "showCustomizerMockups",
  ],
};

export const BLANK_SETTINGS_NAV = [
  {
    id: "product",
    label: "Product",
    items: [
      { id: "general", label: "General", segment: "general" },
      {
        id: "print-methods",
        label: "Print Methods",
        segment: "print-methods",
        badgeKey: "printMethods",
      },
      { id: "dimensions", label: "Dimensions & customs", segment: "dimensions" },
      { id: "add-ons", label: "Add ons", segment: "add-ons" },
      { id: "neck-label", label: "Neck label", segment: "neck-label" },
    ],
  },
  {
    id: "decoration",
    label: "Decoration",
    items: [
      {
        id: "placements",
        label: "Placements",
        segment: "placements",
        badgeKey: "placements",
      },
      {
        id: "design-areas",
        label: "Design Areas",
        segment: "design-areas",
        badgeKey: "designAreas",
      },
    ],
  },
  {
    id: "variants",
    label: "Variants",
    items: [
      {
        id: "variants",
        label: "Colors & Sizes",
        segment: "variants",
        badgeKey: "variants",
      },
      {
        id: "variant-mapping",
        label: "Variant Mapping",
        segment: "variant-mapping",
      },
      {
        id: "suggested-retail",
        label: "Suggested Retail",
        segment: "suggested-retail",
      },
      {
        id: "supplier-match",
        label: "Supplier Match",
        segment: "supplier-match",
      },
    ],
  },
  {
    id: "presentation",
    label: "Presentation",
    items: [
      {
        id: "mockups",
        label: "Mockups",
        segment: "mockups",
        badgeKey: "mockups",
      },
      {
        id: "send-to-channel",
        label: "Send to Channel",
        segment: "send-to-channel",
      },
    ],
  },
];

export const BLANK_SETTINGS_SEGMENTS = BLANK_SETTINGS_NAV.flatMap(
  (g) => g.items.map((i) => i.segment)
);

/**
 * Print products (paper print, stickers and labels, DTF) edit in the print
 * wizard; apparel and 3D edit in the generic settings. The classifier reads
 * the product's resolved production category rather than a hardcoded list of
 * type strings, so every print category routes the same way and a new one
 * needs no edit here. Stored production wins; otherwise the type and sticker
 * type resolve to a preset the same way the create wizard does. The product
 * name is never read (PRINT.1 spec 3.8).
 */
const PRINT_PRODUCTION_CATEGORIES = new Set(["flatPrint", "adhesive", "transfer"]);
const STICKER_TYPE_SET = new Set(["dieCut", "kissCut", "stickerSheet", "rollLabel"]);

function resolveBlankProductionCategory(blankOrType) {
  if (typeof blankOrType === "string") {
    const preset = STICKER_TYPE_SET.has(blankOrType)
      ? presetFor({ stickerType: blankOrType })
      : presetFor({ productType: blankOrType });
    return productionFromPreset(preset)?.production?.category || null;
  }
  const stored = blankOrType?.settings?.production || blankOrType?.production;
  if (stored?.category) return stored.category;
  const preset = presetFor({
    productType:
      blankOrType?.type ||
      blankOrType?.productType ||
      blankOrType?.blankSettings?.type ||
      "",
    stickerType:
      blankOrType?.stickerType ||
      blankOrType?.blankSettings?.stickerType ||
      blankOrType?.settings?.stickerType ||
      "",
    is3DProduct: blankOrType?.is3DProduct,
  });
  return productionFromPreset(preset)?.production?.category || null;
}

export function isRiverrPrintBlank(blankOrType) {
  if (!blankOrType) return false;
  return PRINT_PRODUCTION_CATEGORIES.has(
    resolveBlankProductionCategory(blankOrType)
  );
}

export function riverrPrintEditPath(blankId) {
  if (!blankId) return null;
  return `/blank-products/edit/riverr-product/${blankId}`;
}

function omitNavSegments(groups, segments) {
  const hide = new Set(segments);
  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => !hide.has(item.segment)),
    }))
    .filter((group) => group.items.length > 0);
}

/**
 * Nav groups with Neck label filtered for the current blank. Design Areas
 * shows for every product: it is the print setup for print products.
 */
export function getBlankSettingsNav(
  blankOrType,
  { scentLabels = false, neckLabelEnabled } = {}
) {
  const hide = [];
  const showNeckLabel =
    neckLabelEnabled !== undefined
      ? !!neckLabelEnabled
      : blankHasNeckLabelPlacement(blankOrType);
  if (!showNeckLabel) hide.push("neck-label");
  const base = hide.length
    ? omitNavSegments(BLANK_SETTINGS_NAV, hide)
    : BLANK_SETTINGS_NAV;
  if (!scentLabels) return base;
  return base.map((group) => ({
    ...group,
    items: group.items.map((item) =>
      item.segment === "variants"
        ? { ...item, label: "Scents & Sizes" }
        : item
    ),
  }));
}

export function parseBlankSettingsSegment(pathname) {
  const m = (pathname || "").match(
    /^\/products\/blanks\/[^/]+\/([^/?#]+)/
  );
  return m ? m[1] : null;
}

function isPathActive(pathname, target) {
  if (!target) return false;
  return pathname === target || pathname.startsWith(`${target}/`);
}

export function isBlankSettingsNavItemActive(pathname, item, blankId) {
  if (!item?.segment || !blankId) return false;
  return isPathActive(pathname, blankSettingsPathFor(blankId, item.segment));
}
